import styled from 'styled-components'

import { Layout, Text } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { TrackingProvider } from '../../../../../../domain/Tracking'
import { formatTrackingProviderName } from '../../../../../../presenters/preferredTrackingMethod'
import { useTrackingOnboardingContext } from '../../../TrackingOnboardingContext'

const StyledListItem = styled.li`
  padding-bottom: ${getToken('spacing-2')};
  list-style: decimal;
`

export type ELDFinalizeConnectionContentProps = {
  readonly selectedProvider?: TrackingProvider
}

export default function ELDFinalizeConnectionContent({
  selectedProvider,
}: ELDFinalizeConnectionContentProps) {
  const { hasPreviousTracking } = useTrackingOnboardingContext()

  if (hasPreviousTracking) {
    const formatterName = formatTrackingProviderName(selectedProvider)

    return (
      <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
        <Layout.Stack gap="spacing-4">
          <Text variant="body-md-bold">ELD with {formatterName}</Text>

          <Layout.Stack gap="spacing-6">
            <Text variant="body-md">
              You're about to confirm your preferred connection as {formatterName} ELD.
            </Text>
            <Text variant="body-md">You can update this choice anytime via the tracking card.</Text>
          </Layout.Stack>
        </Layout.Stack>
      </Layout.Group>
    )
  }

  if (selectedProvider === TrackingProvider.MACROPOINT) {
    return (
      <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
        <Layout.Stack gap="spacing-4">
          <Text variant="body-md-bold">Connect to Macropoint ELDs</Text>
          <Text variant="body-md">
            When you connect with your telematics account, a single integration enables you to
            seamlessly send location data from your GPS devices. Use the Macropoint setup to
            complete it and get ready to track your loads.
          </Text>
          <Layout.Group paddingL="spacing-4">
            <Text variant="body-md">
              <ol>
                <StyledListItem>
                  You will be redirected to the Macropoint Carrier Connect page
                </StyledListItem>
                <StyledListItem>
                  Select your ELD/GPS provider from 300+ integrated partners
                </StyledListItem>
                <StyledListItem>
                  Provide authorization credentials (API token, login info, or one-click connect
                  depending on your provider)
                </StyledListItem>
                <StyledListItem>
                  Once you're done, we will be checking the connection. As soon as we get the first
                  ping, we will set your tracking method as connected.
                </StyledListItem>
              </ol>
            </Text>
          </Layout.Group>
        </Layout.Stack>
      </Layout.Group>
    )
  }

  return (
    <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
      <Layout.Stack gap="spacing-4">
        <Text variant="body-md-bold">Connect to Trucker Tools ELDs</Text>
        <Text variant="body-md">
          When you connect with your telematics account, a single integration enables you to
          seamlessly send location data from your GPS devices, use the Trucker Tools setup to
          complete it and get it ready to track your loads.
        </Text>
        <Layout.Group paddingL="spacing-4">
          <Text variant="body-md">
            <ol>
              <StyledListItem>
                You will be redirected to Trucker Tools ELD setup page
              </StyledListItem>
              <StyledListItem>
                Enter your information and submit the connection request
              </StyledListItem>
              <StyledListItem>
                After submitting the request, someone from Trucker Tools will reach out to you and
                complete the connection.
              </StyledListItem>
            </ol>
          </Text>
        </Layout.Group>
      </Layout.Stack>
    </Layout.Group>
  )
}
